// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * PrivacyPage - /privacy
 * Volledige privacyverklaring conform AVG Art. 13/14.
 *
 * Dekt:
 * - Welke gegevens worden opgeslagen (en welke niet)
 * - Lokale opslag uitleg
 * - Rechten van gebruiker
 * - Bewaartermijn
 * - Contactgegevens
 */
import { BackButton } from '@/components/ui/BackButton'
import { Card }       from '@/components/ui/Card'

interface Section { title: string; content: string | string[] }

const SECTIONS: Section[] = [
  {
    title: 'Wie is verantwoordelijk?',
    content: [
      'WilsApp is een hulpmiddel voor levenseindeplanning, ontwikkeld als pilotproduct. De verwerkingsverantwoordelijke voor de verwerking van uw persoonsgegevens is WilsApp.',
      'Contactgegevens verwerkingsverantwoordelijke: info@wilsapp.nl',
      'WilsApp is ontwikkeld door en is eigendom van J.J.S. van der Werf. De software, broncode, vormgeving, teksten en het onderliggende concept worden beschermd door het auteursrecht en andere intellectuele eigendomsrechten.',
      '© 2024–2026 J.J.S. van der Werf. Alle rechten voorbehouden.',
      'Het concept en de ontwikkeling van WilsApp zijn vastgelegd in meerdere i-DEPOT-registraties bij het Benelux-Bureau voor de Intellectuele Eigendom (BOIP).',
    ],
  },
  {
    title: 'Pilotversie',
    content: [
      'Deze versie van WilsApp is uitsluitend bedoeld als pilot- en testversie. De applicatie wordt beschikbaar gesteld voor evaluatie, testen en het verzamelen van gebruikerservaringen.',
      'Deze pilotversie is uitsluitend bedoeld voor persoonlijk gebruik door de aangewezen tester(s). Het is niet toegestaan de applicatie, of onderdelen daarvan, zonder voorafgaande schriftelijke toestemming van de rechthebbende te kopiëren, verspreiden, publiceren, beschikbaar te stellen aan derden of op andere wijze openbaar te maken.',
    ],
  },
  {
    title: 'Welke gegevens worden opgeslagen?',
    content: [
      'WilsApp slaat uitsluitend gegevens op die u zelf invoert, versleuteld op uw eigen apparaat. Dit betreft:',
      '• Naam en geboortedatum (voor profielidentificatie en verificatie)',
      '• Contactgegevens (e-mailadres, telefoonnummer, adres)',
      '• Burgerlijke staat',
      '• Informatie over uw partner, kinderen en huisdieren',
      '• Woning- en bezittingeninformatie',
      '• Financieel overzicht (bankrekeningen, pensioenfonds, verzekeringen)',
      '• Medische wensen en donorregistratiestatus',
      '• Uitvaartwensen',
      '• Notariële wensen (executeur, voogdij)',
      '• Documentlocaties (uitsluitend locatiebeschrijvingen, geen documenten zelf)',
      '• Persoonlijke berichten voor nabestaanden',
      '• Gegevens van aangewezen vertrouwenspersonen (naam, relatie)',
    ],
  },
  {
    title: 'Welke gegevens worden NIET opgeslagen?',
    content: [
      '• Daadwerkelijke documenten, scans of bestandsbijlagen',
      '• Uw wachtwoord - WilsApp heeft hier nooit toegang toe',
      '• Bankpassen, pincodes of inloggegevens van externe diensten',
      '• Locatiedata of browsegeschiedenis',
      '• Gegevens worden niet verstuurd naar WilsApp-servers (pilotversie: volledig lokale opslag)',
      '• Adrescontrole: als u een adres laat invullen, worden alleen uw postcode en huisnummer naar de openbare adresservice van de overheid (PDOK) gestuurd om straat en plaats op te halen. WilsApp slaat hierbij niets bij die service op en deze controle is niet verplicht.',
    ],
  },
  {
    title: 'Hoe worden uw gegevens beveiligd?',
    content: [
      'Uw gegevens worden versleuteld opgeslagen op bankniveau - dit betekent dat uw gegevens zonder uw toegangscode volledig onleesbaar zijn, ook voor WilsApp zelf.',
      'Technische details: AES-256-GCM versleuteling. De sleutelafleiding via PBKDF2-SHA256 (600.000 iteraties) maakt uw wachtwoord extra moeilijk te raden.',
      'WilsApp heeft technisch geen toegang tot uw gegevens. Zonder uw wachtwoord zijn alle gegevens onleesbaar, ook voor WilsApp zelf.',
      'Vertrouwenspersoon-toegang is beveiligd met een unieke sleutelcode én geboortedatum als tweede verificatiefactor.',
    ],
  },
  {
    title: 'Rechtsgrondslag voor verwerking',
    content: [
      'De verwerking van uw gegevens is gebaseerd op uw expliciete toestemming (AVG Art. 6 lid 1 sub a). Voor bijzondere persoonsgegevens (medische wensen, donorregistratie) geldt expliciete toestemming conform AVG Art. 9 lid 2 sub a.',
      'U kunt uw toestemming te allen tijde intrekken door uw account te verwijderen via Instellingen → Alle gegevens wissen.',
    ],
  },
  {
    title: 'Bewaartermijn',
    content: [
      'Uw gegevens worden uitsluitend bewaard op uw eigen apparaat, voor de duur dat u de applicatie actief gebruikt.',
      'Er is geen automatische verwijdering. U kunt uw gegevens op elk moment zelf verwijderen via Instellingen → Alle gegevens wissen.',
      'WilsApp bewaart geen kopieën van uw gegevens op externe servers (pilotversie).',
    ],
  },
  {
    title: 'Uw rechten',
    content: [
      'U heeft de volgende rechten op grond van de AVG:',
      '• Recht op inzage (Art. 15): U kunt uw gegevens inzien door uw profiel en alle modules in WilsApp te bekijken.',
      '• Recht op rectificatie (Art. 16): U kunt uw gegevens op elk moment aanpassen via de wizard of de individuele modules.',
      '• Recht op wissing (Art. 17): U kunt alle gegevens definitief verwijderen via Instellingen → Alle gegevens wissen.',
      '• Recht op beperking (Art. 18): U kunt specifieke modules leeg laten of gegevens verwijderen.',
      '• Recht op bezwaar (Art. 21): U kunt bezwaar maken tegen de verwerking via info@wilsapp.nl.',
      'Voor de uitoefening van uw rechten kunt u contact opnemen via info@wilsapp.nl.',
    ],
  },
  {
    title: 'Vertrouwenspersoon en gegevenstoegang',
    content: [
      'Als u een vertrouwenspersoon aanwijst, ontvangt die persoon een unieke sleutelcode. Met deze sleutelcode én uw geboortedatum kan uw vertrouwenspersoon bepaalde modules inzien.',
      'De modules die een vertrouwenspersoon kan inzien, worden door u bepaald bij het aanwijzen. Financiële gegevens en persoonsgegevens zijn altijd uitgesloten.',
      'Belangrijk: WilsApp verifieert niet of u daadwerkelijk bent overleden voordat uw vertrouwenspersoon toegang krijgt. Het gebruik van de sleutelcode is een vertrouwenshandeling. Geef de sleutelcode alleen af aan iemand die u volledig vertrouwt.',
    ],
  },
  {
    title: 'Klachten',
    content: [
      'Als u meent dat uw privacyrechten worden geschonden, heeft u het recht een klacht in te dienen bij de Autoriteit Persoonsgegevens (AP).',
      'Website: autoriteitpersoonsgegevens.nl - Telefoon: 088 - 1805 250',
    ],
  },
  {
    title: 'Wijzigingen in deze verklaring',
    content: [
      'WilsApp behoudt zich het recht voor deze privacyverklaring aan te passen. Bij significante wijzigingen wordt u hierover geïnformeerd via de applicatie. De huidige versie is altijd beschikbaar via /privacy.',
      'Versie: 1.0 - Pilotversie 2026',
    ],
  },
]

export function PrivacyPage() {
  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <BackButton />

      <div>
        <h1 className="text-xl font-semibold text-ink mb-1">Privacyverklaring</h1>
        <p className="text-sm text-ink-3 leading-relaxed">
          WilsApp verwerkt persoonsgegevens conform de Algemene Verordening Gegevensbescherming (AVG).
          Hieronder leest u welke gegevens worden opgeslagen, waarom en wat uw rechten zijn.
        </p>
      </div>

      {SECTIONS.map((section, i) => (
        <Card key={i}>
          <h2 className="text-sm font-semibold text-ink mb-3">{section.title}</h2>
          <div className="flex flex-col gap-1.5">
            {Array.isArray(section.content)
              ? section.content.map((line, j) => (
                  <p key={j} className="text-xs text-ink-3 leading-relaxed">{line}</p>
                ))
              : <p className="text-xs text-ink-3 leading-relaxed">{section.content}</p>
            }
          </div>
        </Card>
      ))}

      <p className="text-center text-xs text-ink-4 pb-4">
        Vragen? Stuur een e-mail naar info@wilsapp.nl
      </p>
    </div>
  )
}
